import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useAuthOptional } from '@/context/AuthContext';
import { useResolvedFeatureFlags } from '@/hooks/useResolvedFeatureFlags';
import { apiRequest } from '@/lib/queryClient';
import {
  DEFAULT_PLATFORM_SETTINGS,
  LEGACY_DARK_MODE_KEY,
  PLATFORM_SETTINGS_CACHE_KEY,
  normalizeColorTheme,
  normalizeDisplayMode,
  normalizeTextSize,
  type ColorTheme,
  type DisplayMode,
  type PlatformSettings,
  type ResolvedMode,
  type TextSize,
} from '../../../shared/platform-settings';

/**
 * AR-393 · The whole of platform-settings state: colour theme, display mode and
 * text size.
 *
 * These are attached to the member's login, not the device, so the member
 * record is the source of truth. localStorage keeps a cache of it only so
 * `client/public/loaders/platform-settings.js` can stamp the settings before
 * first paint; the server's values overwrite that cache on every load.
 *
 * Every writer goes through one setter per setting — the Settings module page
 * and the user menu's Dark mode row both land on `setDisplayMode`, so the two
 * controls cannot disagree. `setIsDarkMode` is kept as a thin alias over it for
 * the surfaces that predate the three-valued mode.
 *
 * Gated on `proMigrationSettings`, the Settings module's existing migration
 * sub-flag. With the flag off, display mode behaves exactly as it did before:
 * a single boolean in localStorage('darkMode'), no server round-trip.
 */

interface ThemeContextType {
  /** The RESOLVED appearance — `system` has already been resolved against the device. */
  isDarkMode: boolean;
  /** Alias for `setDisplayMode('dark' | 'light')`. */
  setIsDarkMode: (value: boolean) => void;
  colorTheme: ColorTheme;
  setColorTheme: (value: ColorTheme) => void;
  /** The member's CHOICE — light | dark | system. */
  displayMode: DisplayMode;
  setDisplayMode: (value: DisplayMode) => void;
  /** What that choice currently resolves to. Always light or dark. */
  resolvedMode: ResolvedMode;
  textSize: TextSize;
  setTextSize: (value: TextSize) => void;
  /** True once the member's saved settings have been read from the server. */
  platformSettingsLoaded: boolean;
  lightLogo: string;
  setLightLogo: (value: string) => void;
  darkLogo: string;
  setDarkLogo: (value: string) => void;
  favicon: string;
  setFavicon: (value: string) => void;
}

const defaultLightLogo = './assets/aivia-logo-blue-transparent-crop.png';
const defaultDarkLogo = './assets/aivia-logo-blue-transparent-crop.png';
const defaultFavicon = './assets/aivia-favicon.png';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const DARK_QUERY = '(prefers-color-scheme: dark)';

const prefersDark = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(DARK_QUERY).matches;
};

const resolveMode = (mode: DisplayMode): ResolvedMode =>
  mode === 'system' ? (prefersDark() ? 'dark' : 'light') : mode;

// null means this browser has never applied the member's settings — which is a
// different thing from "cached the defaults", and the only case in which the
// legacy darkMode boolean still says anything about the appearance.
const readCache = (): PlatformSettings | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(PLATFORM_SETTINGS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return {
      colorTheme: normalizeColorTheme(parsed?.colorTheme),
      displayMode: normalizeDisplayMode(parsed?.displayMode),
      textSize: normalizeTextSize(parsed?.textSize),
    };
  } catch {
    return null;
  }
};

/**
 * The legacy boolean, read ONCE per page load before any effect can run.
 *
 * The provider itself rewrites this key while the migration flag is off, and
 * `isEnabled` reports false until the flag request resolves — so a value read
 * later may be one this code just wrote. Adoption must only ever consider what
 * was on the device when the page opened.
 */
const readLegacyDarkMode = (): boolean | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(LEGACY_DARK_MODE_KEY);
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return null;
  } catch {
    return null;
  }
};

const writeCache = (settings: PlatformSettings) => {
  try {
    window.localStorage.setItem(
      PLATFORM_SETTINGS_CACHE_KEY,
      JSON.stringify(settings),
    );
  } catch {
    /* private mode / blocked storage — the settings still apply this session */
  }
};

interface PreferencesResponse {
  colorTheme?: unknown;
  displayMode?: unknown;
  textSize?: unknown;
  storedPlatformSettings?: {
    colorTheme: string | null;
    displayMode: string | null;
    textSize: string | null;
  };
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const auth = useAuthOptional();
  const isAuthenticated = auth?.isAuthenticated ?? false;
  const userId = auth?.user?.id ?? null;
  const { isEnabled, isLoading: flagsLoading } = useResolvedFeatureFlags();
  const settingsEnabled = isEnabled('proMigrationSettings');

  // What the device carried when the page opened. Captured at first render so
  // the legacy write effect below cannot feed its own output back into the
  // one-time adoption.
  const legacyDarkModeAtBoot = useRef<boolean | null>(readLegacyDarkMode());

  // Seeded from the same cache the pre-paint loader read, so the first render
  // agrees with what is already stamped on the document. The legacy boolean is
  // only consulted when there is no cache — otherwise a stale `darkMode=false`
  // would override a cached dark theme and produce the flash this provider
  // exists to prevent.
  const [settings, setSettings] = useState<PlatformSettings>(() => {
    const cached = readCache();
    if (cached) return cached;
    const legacy = legacyDarkModeAtBoot.current;
    if (legacy === null) return DEFAULT_PLATFORM_SETTINGS;
    return { ...DEFAULT_PLATFORM_SETTINGS, displayMode: legacy ? 'dark' : 'light' };
  });
  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() =>
    resolveMode(settings.displayMode),
  );
  const [platformSettingsLoaded, setPlatformSettingsLoaded] = useState(false);
  // Kept in step during render so a setter can capture the pre-change value
  // synchronously, without depending on when React runs a state updater.
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const saveSeq = useRef(0);
  const latestSaveByKey = useRef<Partial<Record<keyof PlatformSettings, number>>>({});

  const [lightLogo, setLightLogo] = useState<string>(() => {
    // Clear old logo cache and use new default
    localStorage.removeItem('lightLogo');
    return defaultLightLogo;
  });

  const [darkLogo, setDarkLogo] = useState<string>(() => {
    // Clear old logo cache and use new default
    localStorage.removeItem('darkLogo');
    return defaultDarkLogo;
  });

  const [favicon, setFavicon] = useState<string>(() => {
    const saved = localStorage.getItem('favicon');
    return saved || defaultFavicon;
  });

  // ── Apply ────────────────────────────────────────────────────────────────
  // One effect stamps the document, so every writer produces the same result
  // and a change re-skins the whole app rather than the page that made it.
  useEffect(() => {
    const html = document.documentElement;
    html.dataset.mode = resolvedMode;
    html.dataset.textsize = settings.textSize;
    html.classList.toggle('dark', resolvedMode === 'dark');
    document.body.dataset.theme = settings.colorTheme;
  }, [settings.colorTheme, settings.textSize, resolvedMode]);

  useEffect(() => {
    setResolvedMode(resolveMode(settings.displayMode));
  }, [settings.displayMode]);

  // `system` keeps following the device: flipping the OS between light and dark
  // re-skins the app without the member touching the setting.
  useEffect(() => {
    if (settings.displayMode !== 'system') return;
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => setResolvedMode(prefersDark() ? 'dark' : 'light');
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [settings.displayMode]);

  useEffect(() => {
    if (settingsEnabled) {
      writeCache(settings);
      return;
    }
    // Legacy behaviour, but only once we know the flag is genuinely off for a
    // signed-in member. `isEnabled` reports false while the flag request is in
    // flight, and writing here in that window would invent a display-mode
    // choice that the member never made.
    if (flagsLoading || !isAuthenticated) return;
    try {
      localStorage.setItem(LEGACY_DARK_MODE_KEY, String(resolvedMode === 'dark'));
    } catch {
      /* ignore */
    }
  }, [settings, resolvedMode, settingsEnabled, flagsLoading, isAuthenticated]);

  // ── Load from the member record ──────────────────────────────────────────
  const loadedForUser = useRef<number | null>(null);
  useEffect(() => {
    if (!settingsEnabled || !isAuthenticated || userId === null) return;
    if (loadedForUser.current === userId) return;
    loadedForUser.current = userId;

    let cancelled = false;
    let finished = false;
    (async () => {
      try {
        const prefs = await apiRequest<PreferencesResponse>(
          'GET',
          '/api/user/preferences',
        );
        if (cancelled) return;

        const saved: PlatformSettings = {
          colorTheme: normalizeColorTheme(prefs?.colorTheme),
          displayMode: normalizeDisplayMode(prefs?.displayMode),
          textSize: normalizeTextSize(prefs?.textSize),
        };

        // A device may still carry the single darkMode boolean. Adopt it ONCE,
        // and only when the member has never saved a display mode — a saved
        // setting always wins over a leftover key, on every device.
        const legacy = legacyDarkModeAtBoot.current;
        const neverChoseMode =
          prefs?.storedPlatformSettings?.displayMode == null;
        const adopted: DisplayMode | null =
          neverChoseMode && legacy !== null
            ? legacy
              ? 'dark'
              : 'light'
            : null;
        if (adopted) saved.displayMode = adopted;

        setSettings(saved);
        setPlatformSettingsLoaded(true);

        // The key is only dropped once the record actually holds a mode —
        // otherwise a failed PATCH would lose the member's appearance with
        // nothing left on the device to recover it from.
        if (adopted) {
          await apiRequest('PATCH', '/api/user/preferences', {
            displayMode: adopted,
          });
        }
        try {
          localStorage.removeItem(LEGACY_DARK_MODE_KEY);
          legacyDarkModeAtBoot.current = null;
        } catch {
          /* ignore */
        }
      } catch (error) {
        console.error('[ThemeProvider] Failed to load platform settings', error);
        if (!cancelled) setPlatformSettingsLoaded(true);
      } finally {
        finished = true;
      }
    })();

    return () => {
      cancelled = true;
      // The guard is set before the request completes. If this effect is torn
      // down mid-flight — the flag query re-resolving, say — the next run must
      // be allowed to try again rather than treat the member as loaded.
      if (!finished && loadedForUser.current === userId) {
        loadedForUser.current = null;
      }
    };
  }, [settingsEnabled, isAuthenticated, userId]);

  // ── Setters ──────────────────────────────────────────────────────────────
  // Saved on change — there is no separate Save step.
  const persist = useCallback(
    (patch: Partial<PlatformSettings>) => {
      // Applied optimistically — a theme switch that waited for the network
      // would feel broken. If the save fails the change is rolled back, so the
      // screen and the cache never keep a value the record does not hold.
      const previous = settingsRef.current;
      setSettings((current) => ({ ...current, ...patch }));
      if (!settingsEnabled || !isAuthenticated) return;
      const keys = Object.keys(patch) as (keyof PlatformSettings)[];
      const seq = ++saveSeq.current;
      for (const key of keys) latestSaveByKey.current[key] = seq;
      apiRequest('PATCH', '/api/user/preferences', patch).catch((error) => {
        console.error('[ThemeProvider] Failed to save platform settings', error);
        // Only undo keys this save still owns — a newer save of the same key
        // holds a newer optimistic value that must stand.
        const owned = keys.filter((key) => latestSaveByKey.current[key] === seq);
        if (owned.length === 0) return;
        setSettings((current) => {
          const restored = { ...current };
          for (const key of owned) {
            (restored[key] as PlatformSettings[typeof key]) = previous[key];
          }
          return restored;
        });
      });
    },
    [settingsEnabled, isAuthenticated],
  );

  const setColorTheme = useCallback(
    (value: ColorTheme) => persist({ colorTheme: normalizeColorTheme(value) }),
    [persist],
  );

  const setDisplayMode = useCallback(
    (value: DisplayMode) => persist({ displayMode: normalizeDisplayMode(value) }),
    [persist],
  );

  const setTextSize = useCallback(
    (value: TextSize) => persist({ textSize: normalizeTextSize(value) }),
    [persist],
  );

  const setIsDarkMode = useCallback(
    (value: boolean) => setDisplayMode(value ? 'dark' : 'light'),
    [setDisplayMode],
  );

  // Save logo settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('lightLogo', lightLogo);
  }, [lightLogo]);

  useEffect(() => {
    localStorage.setItem('darkLogo', darkLogo);
  }, [darkLogo]);

  useEffect(() => {
    localStorage.setItem('favicon', favicon);
    // Update favicon link in document head
    const linkElement = document.querySelector(
      'link[rel="icon"]',
    ) as HTMLLinkElement;
    if (linkElement) {
      linkElement.href = favicon;
    }
  }, [favicon]);

  const value = useMemo<ThemeContextType>(
    () => ({
      isDarkMode: resolvedMode === 'dark',
      setIsDarkMode,
      colorTheme: settings.colorTheme,
      setColorTheme,
      displayMode: settings.displayMode,
      setDisplayMode,
      resolvedMode,
      textSize: settings.textSize,
      setTextSize,
      platformSettingsLoaded,
      lightLogo,
      setLightLogo,
      darkLogo,
      setDarkLogo,
      favicon,
      setFavicon,
    }),
    [
      resolvedMode,
      setIsDarkMode,
      settings,
      setColorTheme,
      setDisplayMode,
      setTextSize,
      platformSettingsLoaded,
      lightLogo,
      darkLogo,
      favicon,
    ],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/** `useTheme` for components that may render outside the provider (AR-409). */
export function useThemeOptional(): ThemeContextType | undefined {
  return useContext(ThemeContext);
}
